import React, { useEffect, useState, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import type { StudentProfile, PortalClass } from '../../types/portal';
import { Search, UserCheck, UserX, Edit2, X, Check, Plus, Loader2, Trash2 } from 'lucide-react';

/** "Gesehen"-Marker: Schüler, die nach diesem Zeitpunkt registriert sind, gelten als neu */
const SEEN_KEY = 'arabicroots:admin-seen:portal-students';

const cardClass =
  'bg-white rounded-[24px] border border-[#eadfd6] shadow-[0_10px_35px_rgba(0,0,0,0.05)]';
const inputClass =
  'w-full px-4 py-3 border border-[#ddd6cf] rounded-2xl focus:ring-2 focus:ring-[#970000]/10 focus:border-[#970000] outline-none transition-colors duration-200 bg-white';
const primaryBtn =
  'inline-flex items-center px-4 py-2 rounded-full bg-[#970000] text-white hover:bg-[#7a0000] transition-colors duration-200 font-medium text-sm';
const secondaryBtn =
  'inline-flex items-center px-4 py-2 rounded-full border border-[#d9c9bd] text-[#5f5148] hover:bg-[#f8f3ef] transition-colors duration-200 font-medium text-sm';

interface StudentWithClasses extends StudentProfile {
  classIds: string[];
  email?: string;
}

const StudentManager: React.FC = () => {
  const [students, setStudents] = useState<StudentWithClasses[]>([]);
  const [classes, setClasses] = useState<PortalClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<'admin' | 'student'>('student');
  const [editActive, setEditActive] = useState(true);
  const [addingClassId, setAddingClassId] = useState('');
  const [savingClass, setSavingClass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [opError, setOpError] = useState('');
  const [classFilter, setClassFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Beim Öffnen einmalig den alten Marker merken (für die Badges dieser Sitzung)
  // und direkt auf jetzt setzen, damit die Badges beim nächsten Besuch weg sind.
  const [newSince] = useState<string | null>(() => {
    try {
      const prev = localStorage.getItem(SEEN_KEY);
      localStorage.setItem(SEEN_KEY, new Date().toISOString());
      return prev;
    } catch {
      return null;
    }
  });

  const isNew = (createdAt: string) => !newSince || createdAt > newSince;

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    setOpError('');
    try {
      const [{ data: sData, error: sErr }, { data: cData, error: cErr }, { data: mData, error: mErr }] = await Promise.all([
        supabase.from('student_profiles').select('*').eq('role', 'student').order('created_at', { ascending: false }),
        supabase.from('portal_classes').select('*').order('title'),
        supabase.from('portal_class_members').select('class_id, student_id').eq('is_active', true),
      ]);
      if (sErr) throw sErr;
      if (cErr) throw cErr;
      if (mErr) throw mErr;

      const membershipMap: Record<string, string[]> = {};
      for (const m of mData ?? []) {
        const row = m as { class_id: string; student_id: string };
        if (!membershipMap[row.student_id]) membershipMap[row.student_id] = [];
        membershipMap[row.student_id].push(row.class_id);
      }

      const enrichedStudents = (sData ?? []).map(s => ({
        ...(s as StudentProfile),
        classIds: membershipMap[(s as StudentProfile).id] ?? [],
      }));

      setStudents(enrichedStudents);
      setClasses((cData ?? []) as PortalClass[]);
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      setOpError('Schülerdaten konnten nicht geladen werden. Bitte Seite neu laden.');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return students.filter(s => {
      const matchesSearch =
        `${s.first_name} ${s.last_name}`.toLowerCase().includes(q) ||
        (s.display_name ?? '').toLowerCase().includes(q) ||
        (s.email ?? '').toLowerCase().includes(q);

      const matchesClass =
        classFilter === 'all' ||
        (classFilter === 'none' ? s.classIds.length === 0 : s.classIds.includes(classFilter));

      const matchesStatus =
        statusFilter === 'all' || (statusFilter === 'active' ? s.is_active : !s.is_active);

      return matchesSearch && matchesClass && matchesStatus;
    });
  }, [students, search, classFilter, statusFilter]);

  const classMap = useMemo(
    () => Object.fromEntries(classes.map(c => [c.id, c])),
    [classes]
  );

  const activeClasses = useMemo(() => classes.filter(c => c.is_active), [classes]);

  const startEdit = (s: StudentWithClasses) => {
    setEditingId(s.id);
    setEditRole(s.role);
    setEditActive(s.is_active);
    setAddingClassId('');
  };

  const saveProfile = async (id: string) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('student_profiles')
        .update({ role: editRole, is_active: editActive, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;

      setStudents(prev =>
        prev.map(s =>
          s.id === id ? { ...s, role: editRole, is_active: editActive } : s
        )
      );
      setEditingId(null);
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      setOpError('Fehler beim Speichern des Profils. Bitte erneut versuchen.');
    } finally {
      setSaving(false);
    }
  };

  const addClassToStudent = async (studentId: string, classId: string) => {
    if (!classId) return;
    setSavingClass(true);
    try {
      const { error } = await supabase.from('portal_class_members').upsert(
        { class_id: classId, student_id: studentId, is_active: true },
        { onConflict: 'class_id,student_id' }
      );
      if (error) throw error;

      const { data: verifyRows, error: verifyError } = await supabase
        .from('portal_class_members')
        .select('class_id')
        .eq('student_id', studentId)
        .eq('class_id', classId)
        .eq('is_active', true);
      if (verifyError) throw verifyError;

      if (!(verifyRows ?? []).length) throw new Error('membership_not_persisted');

      await load();
      setAddingClassId('');
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      setOpError('Fehler beim Hinzufügen der Klasse. Bitte erneut versuchen.');
    } finally {
      setSavingClass(false);
    }
  };

  const removeClassFromStudent = async (studentId: string, classId: string) => {
    const className = classMap[classId]?.title ?? 'diese Klasse';
    if (!confirm(`Schüler aus "${className}" entfernen?`)) return;
    if (savingClass) return;

    setSavingClass(true);
    try {
      const { error } = await supabase
        .from('portal_class_members')
        .delete()
        .eq('student_id', studentId)
        .eq('class_id', classId);
      if (error) throw error;

      setStudents(prev =>
        prev.map(s =>
          s.id === studentId
            ? { ...s, classIds: s.classIds.filter(id => id !== classId) }
            : s
        )
      );
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      setOpError('Fehler beim Entfernen aus der Klasse. Bitte erneut versuchen.');
    } finally {
      setSavingClass(false);
    }
  };

  const deleteStudent = async (s: StudentWithClasses) => {
    const ok = confirm(
      `${s.first_name} ${s.last_name} wirklich aus dem Portal löschen?\n\n` +
      'Entfernt das Profil und alle Klassenzuordnungen. Der Schüler verliert sofort den Zugang. ' +
      'Das Login-Konto selbst kann aus Sicherheitsgründen nur direkt in Supabase gelöscht werden.'
    );
    if (!ok) return;
    setDeletingId(s.id);
    try {
      // Klassenzuordnungen hängen an auth.users, nicht am Profil → explizit löschen
      const { error: mErr } = await supabase
        .from('portal_class_members')
        .delete()
        .eq('student_id', s.id);
      if (mErr) throw mErr;

      const { error: pErr } = await supabase
        .from('student_profiles')
        .delete()
        .eq('id', s.id);
      if (pErr) throw pErr;

      setStudents(prev => prev.filter(x => x.id !== s.id));
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      setOpError('Fehler beim Löschen des Schülers. Bitte erneut versuchen.');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleActive = async (id: string, current: boolean) => {
    setTogglingId(id);
    try {
      const { error } = await supabase
        .from('student_profiles')
        .update({ is_active: !current, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      setStudents(prev => prev.map(s => (s.id === id ? { ...s, is_active: !current } : s)));
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      setOpError('Fehler beim Ändern des Status. Bitte erneut versuchen.');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 mb-1">
          Portal
        </p>
        <h2 className="text-3xl font-bold text-[#1f1f1f]">Schüler</h2>
      </div>

      {opError && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{opError}</p>
          <button onClick={() => setOpError('')} className="text-red-400 hover:text-red-600 text-xs font-medium shrink-0">Schließen</button>
        </div>
      )}

      {/* Suche + Filter */}
      <div className={cardClass + ' p-4'}>
        <div className="grid md:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9b9490]" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Schüler suchen…"
              className={inputClass + ' pl-10'}
            />
          </div>
          <select
            value={classFilter}
            onChange={e => setClassFilter(e.target.value)}
            className={inputClass}
          >
            <option value="all">Alle Klassen</option>
            <option value="none">Ohne Klasse</option>
            {classes.map(c => (
              <option key={c.id} value={c.id}>
                {c.title}{c.archived_at ? ' (archiviert)' : ''}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
            className={inputClass}
          >
            <option value="all">Alle Status</option>
            <option value="active">Aktiv</option>
            <option value="inactive">Inaktiv</option>
          </select>
        </div>
        <p className="mt-3 text-xs text-[#9b9490]">
          {filtered.length} von {students.length} Schülern
          {filtered.filter(s => isNew(s.created_at)).length > 0 &&
            ` · ${filtered.filter(s => isNew(s.created_at)).length} neu seit deinem letzten Besuch`}
        </p>
      </div>

      {/* Table */}
      <div className={cardClass + ' overflow-hidden'}>
        {loading ? (
          <div className="p-10 text-center text-[#6b6b6b]">Wird geladen…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#6b6b6b]">Keine Schüler gefunden.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#eadfd6]">
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Name</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Klassen</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Rolle</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Status</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => {
                  const isEditing = editingId === s.id;
                  const availableClasses = activeClasses.filter(c => !s.classIds.includes(c.id));
                  return (
                    <tr key={s.id} className="border-b border-[#eadfd6] hover:bg-[#faf8f6] align-top">
                      <td className="p-4">
                        <p className="font-medium text-[#1f1f1f] flex items-center gap-2">
                          {s.first_name} {s.last_name}
                          {isNew(s.created_at) && (
                            <span className="inline-flex items-center rounded-full bg-[#970000] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                              Neu
                            </span>
                          )}
                        </p>
                        {s.display_name && (
                          <p className="text-xs text-[#6b6b6b]">{s.display_name}</p>
                        )}
                        <p className="text-xs text-[#9b9490] mt-0.5">
                          ID: {s.id.slice(0, 8)}… · seit {new Date(s.created_at).toLocaleDateString('de-DE')}
                        </p>
                      </td>

                      {/* Klassen */}
                      <td className="p-4 min-w-[260px]">
                        {s.classIds.length === 0 && !isEditing ? (
                          <span className="text-xs text-[#9b9490] italic">
                            Keine Klasse zugeordnet
                          </span>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {s.classIds.map(cid => {
                              const cls = classMap[cid];
                              if (!cls) return null;
                              const isArchived = !!cls.archived_at;
                              return (
                                <span
                                  key={cid}
                                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg font-medium ${
                                    isArchived
                                      ? 'bg-[#f5f0ea] text-[#8a8178] border border-[#e8e2db]'
                                      : 'bg-[#970000]/10 text-[#970000] border border-[#970000]/20'
                                  }`}
                                  title={isArchived ? 'Archivierter Kurs' : 'Aktiver Kurs'}
                                >
                                  {cls.title}
                                  {isEditing && (
                                    <button
                                      onClick={() => removeClassFromStudent(s.id, cid)}
                                      disabled={savingClass}
                                      className="hover:bg-red-100 rounded p-0.5 -mr-0.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                      title="Entfernen"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                        )}

                        {isEditing && availableClasses.length > 0 && (
                          <div className="flex gap-1.5 mt-2">
                            <select
                              value={addingClassId}
                              onChange={e => setAddingClassId(e.target.value)}
                              disabled={savingClass}
                              className="flex-1 px-2 py-1 border border-[#ddd6cf] rounded-xl text-xs bg-white"
                            >
                              <option value="">Klasse hinzufügen…</option>
                              {availableClasses.map(c => (
                                <option key={c.id} value={c.id}>
                                  {c.title}{c.archived_at ? ' (archiviert)' : ''}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => addClassToStudent(s.id, addingClassId)}
                              disabled={!addingClassId || savingClass}
                              className="inline-flex items-center px-2 py-1 rounded-xl bg-[#970000] text-white text-xs disabled:opacity-50"
                            >
                              {savingClass ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Plus className="h-3 w-3" />
                              )}
                            </button>
                          </div>
                        )}
                      </td>

                      {/* Rolle */}
                      <td className="p-4">
                        {isEditing ? (
                          <select
                            value={editRole}
                            onChange={e => setEditRole(e.target.value as 'admin' | 'student')}
                            className="px-2 py-1 border border-[#ddd6cf] rounded-xl text-sm"
                          >
                            <option value="student">Schüler</option>
                            <option value="admin">Admin</option>
                          </select>
                        ) : (
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              s.role === 'admin'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {s.role === 'admin' ? 'Admin' : 'Schüler'}
                          </span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="p-4">
                        {isEditing ? (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editActive}
                              onChange={e => setEditActive(e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-sm">Aktiv</span>
                          </label>
                        ) : (
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              s.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {s.is_active ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        )}
                      </td>

                      {/* Aktionen */}
                      <td className="p-4">
                        {isEditing ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveProfile(s.id)}
                              disabled={saving}
                              className={primaryBtn}
                            >
                              {saving ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              ) : (
                                <Check className="h-3.5 w-3.5 mr-1" />
                              )}
                              Fertig
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <button onClick={() => startEdit(s)} className={secondaryBtn}>
                              <Edit2 className="h-3.5 w-3.5 mr-1" />
                              Bearbeiten
                            </button>
                            <button
                              onClick={() => toggleActive(s.id, s.is_active)}
                              disabled={togglingId === s.id}
                              className={secondaryBtn + ' disabled:opacity-50'}
                              title={s.is_active ? 'Deaktivieren' : 'Aktivieren'}
                            >
                              {togglingId === s.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : s.is_active ? (
                                <UserX className="h-3.5 w-3.5 text-red-500" />
                              ) : (
                                <UserCheck className="h-3.5 w-3.5 text-green-600" />
                              )}
                            </button>
                            <button
                              onClick={() => deleteStudent(s)}
                              disabled={deletingId === s.id}
                              className={secondaryBtn + ' disabled:opacity-50 hover:!bg-red-50 hover:!border-red-200'}
                              title="Schüler löschen"
                            >
                              {deletingId === s.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              )}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentManager;
